/**
 * The home page (decision 77; part 11d log, table H): the app's header, the book last written in under "Pick up where you
 * left off", My books as a shelf of covers, and Shared with me. The book in the banner is the workspace's open book (the
 * one opened last), so Continue writing only shows the writer: its text is already open, under this page.
 */
import { createMemo, mergeProps } from 'solid-js';
import type { BookSummary } from '../shell/workspace.ts';
import { SHELF_SORTS } from '../shell/settings.ts';
import type { ShelfSort } from '../shell/settings.ts';
import { editedWhen, shelfOrder } from '../shell/shelf.ts';
import { coverColourOf, coverIndex, coverPhotoUrl } from './cover.ts';
import { ageText } from './backup.ts';
import { BundleImportItem } from './bundle-import.ts';
import { shownBook, shownTitle } from './chapter-list.ts';
import { el, indexed, when, whenSome } from '../ui/dom.ts';
import { ExportMenu } from './export-menu.ts';
import { ImportItem } from './import.ts';
import { TrialChip } from './account.ts';
import { isGuest } from '../account/status.ts';
import { onlyShared, SharedSection } from './shared.ts';
import type { Child } from '../ui/dom.ts';
import AppHeader from '../ui/AppHeader.ts';
import BookCard from '../ui/BookCard.ts';
import BookRow from '../ui/BookRow.ts';
import Button from '../ui/Button.ts';
import UiCover from '../ui/Cover.ts';
import IconButton from '../ui/IconButton.ts';
import Menu from '../ui/Menu.ts';
import type { MenuEntry, MenuTriggerProps } from '../ui/Menu.ts';
import NavLinks from '../ui/NavLinks.ts';
import type { NavLink } from '../ui/NavLinks.ts';
import NewBookTile from '../ui/NewBookTile.ts';
import ProgressBar from '../ui/ProgressBar.ts';
import SearchField from '../ui/SearchField.ts';
import Select from '../ui/Select.ts';
import { component } from '../ui/solid.ts';
import type { Shell } from './shell.ts';

/** Part 11d, R5: puts the focus on the home page's heading, or on its books when no book is open to head it. */
export function focusHome(): void {
  for (const selector of ['#home-title', '#home-books']) {
    const target = document.querySelector(selector);
    if (!(target instanceof HTMLElement)) continue;
    target.focus();
    if (document.activeElement === target) return;
  }
}

/** H1: the writer; the caret goes into the open chapter's text as it shows (R5). */
export function continueWriting(shell: Shell): void {
  shell.go('write');
}

/**
 * H7: opens a book in the writer: at once when it is the open one, else once the workspace has opened it, the caret then
 * in its text (the writer may be showing already, Continue writing pressed meanwhile, with the closing book's editor).
 */
function openBook(shell: Shell, bookId: string): void {
  if (bookId === shell.workspace.openBookId()) continueWriting(shell);
  else {
    void shell.workspace.openBook(bookId).then(() => {
      continueWriting(shell);
      shell.focusEditor();
    });
  }
}

/** H3: a feature not built yet, named, and said to be coming later. */
function laterWords(shell: Shell, name: string): Child {
  return el('span', { class: 'later' }, name, ' ', el('small', {}, shell.t('home.later')));
}

/**
 * H9 (PLAN.md §3, step 2: the library's Cover): a book's cover: its colour (the same on every device, a token per
 * colour) and its title, and on the banner's its subtitle and author; its photo over it once read, the title set on it.
 * `book` is read as it changes (a rename, another book in its place), the cover staying.
 */
function coverOf(shell: Shell, book: () => BookSummary): { readonly index: number; readonly bg: string; readonly ink: string; readonly src: () => string | null } {
  const index = (): number => coverIndex(coverColourOf(book().id, book().metadata));
  const src = coverPhotoUrl(shell, () => book().id, () => book().metadata?.cover ?? null);
  return {
    get index() {
      return index();
    },
    get bg() {
      return `var(--cover-${index()})`;
    },
    get ink() {
      return `var(--cover-${index()}-ink)`;
    },
    src,
  };
}

function BannerCover(shell: Shell, book: () => BookSummary): Child {
  const cover = coverOf(shell, book);
  return component(UiCover, {
    size: 'lg',
    class: 'lib-hero__cover',
    decorative: true,
    overlayTitle: true,
    // Part 11h, BW10: which colour the book has, for the tests and the eye alike.
    get 'data-cover'() {
      return String(cover.index);
    },
    get bg() {
      return cover.bg;
    },
    get ink() {
      return cover.ink;
    },
    get src() {
      return cover.src() ?? undefined;
    },
    get kicker() {
      return book().metadata?.subtitle ?? undefined;
    },
    get title() {
      return shownBook(shell, book());
    },
    get author() {
      return book().metadata?.author ?? undefined;
    },
  });
}

/** H10; part 11h, BW1: New book opens its window, which makes the book and opens it in the writer. */
function createBook(shell: Shell): void {
  shell.ui.setDialog({ kind: 'new-book' });
}

/** H2: the imports, a Word or Markdown file and a bundle, each a menu's item, with its file picker to go beside the menu. */
function imports(shell: Shell): { readonly items: MenuEntry[]; readonly pickers: Child[] } {
  const { t } = shell;
  const file = ImportItem(shell, { label: t('home.importFile'), hint: t('home.importFileHint') });
  const bundle = BundleImportItem(shell, { label: t('home.importBundle'), hint: t('home.importBundleHint') });
  return { items: [file.item, bundle.item], pickers: [file.picker, bundle.picker] };
}

/**
 * H2: a menu of the header (the library's Menu, its button the library's Button with its hook class), off while the
 * books can't change.
 */
function HomeMenu(shell: Shell, label: string, variant: 'primary' | 'secondary', hook: string, items: readonly MenuEntry[]): Child {
  return component(Menu, {
    label,
    // The menu's own hook: `new-book-start-menu` / `import-start-menu`, so the page can lay it out.
    class: `${hook}-menu`,
    items,
    trigger: (props: MenuTriggerProps) =>
      component(
        Button,
        mergeProps(props, {
          variant,
          class: hook,
          get disabled() {
            return !shell.workspace.writable();
          },
          children: label,
        }),
      ),
  });
}

/** Part 36a: the settings page at its Account section, its heading focused (SL3's jump). */
export function openAccountSettings(shell: Shell): void {
  shell.ui.setSettingsAt('settings-account');
  shell.go('settings');
}


/**
 * H2, H3: the header: the wordmark, the pages (the one shown `current`; part 38d: Sharing once this build and account
 * can share), and the tools.
 */
export function Header(shell: Shell, current: 'home' | 'sharing' | 'shared' | (() => 'home' | 'shared') = 'home'): Child {
  const { t, workspace } = shell;
  const page = (): 'home' | 'sharing' | 'shared' => (typeof current === 'function' ? current() : current);
  // Part 38c: a guest who never started a trial writes no books (decision 75): no New book, Import or Sharing.
  const guest = (): boolean => isGuest(shell.account.status());
  // PLAN.md §3, step 2: the library's AppHeader, NavLinks, SearchField and buttons; the menus stay the app's until the
  // menus step (their items must stay on the page while closed: the file pickers in them, and the tests that count them).
  const links = (): NavLink[] => [
    // Deep-fix r5 (tenet 3): My books stays while the device holds books of its own (written before the guest's account).
    ...(onlyShared(shell) ? [] : [{ href: '#/', label: t('home.myBooks'), current: page() === 'home' }]),
    // Part 38c: a guest shares nothing of their own (decision 63: only paid accounts share).
    ...(guest() ? [] : [shell.sharing.configured() ? { href: '#/sharing', class: 'go-sharing', label: t('home.sharing'), current: page() === 'sharing' } : { label: laterWords(shell, t('home.sharing')) }]),
    { href: '#/shared', class: 'go-shared', label: t('home.shared'), current: page() === 'shared' },
  ];
  return component(AppHeader, {
    class: 'home-header',
    href: '#/',
    wordmark: t('app.name'),
    label: t('home.nav'),
    get nav() {
      return component(NavLinks, {
        get items() {
          return links();
        },
      });
    },
    get search() {
      return component(SearchField, { disabled: true, 'aria-label': t('home.search'), placeholder: t('home.laterName', { name: t('home.search') }) });
    },
    get actions() {
      return [
        // H2: on a narrow screen Import moves into New book, a menu there (the design's phone header); one of each, never two.
        // Part 38c: a guest has no books of their own to make or import.
        when(
          () => !guest() && shell.mode() === 'narrow',
          () => {
            const { items, pickers } = imports(shell);
            return [
              HomeMenu(shell, t('book.new'), 'primary', 'new-book-start', [
                {
                  class: 'new-book',
                  label: t('home.newEmpty'),
                  hint: t('home.newEmptyHint'),
                  dialog: true,
                  get disabled() {
                    return !workspace.writable();
                  },
                  onSelect: () => {
                    createBook(shell);
                  },
                },
                { separator: true },
                ...items,
              ]),
              ...pickers,
            ];
          },
          // Part 41a deep-fix r1: its own `when`, as `when` makes a fallback once: the account turning guest or trial while
          // a wide Home stays open changes the header.
          () =>
            when(
              () => !guest(),
              () => {
                const { items, pickers } = imports(shell);
                return [
                  HomeMenu(shell, t('home.import'), 'secondary', 'import-start', items),
                  ...pickers,
                  // One primary action per view (PLAN.md §4, decision 77): Continue writing is the page's; New book is secondary.
                  component(Button, {
                    class: 'new-book',
                    get disabled() {
                      return !workspace.writable();
                    },
                    onClick: () => {
                      createBook(shell);
                    },
                    children: t('book.new'),
                  }),
                ];
              },
            ),
        ),
        // Part 36a: the trial's days left, before the settings (the design's empty library header).
        TrialChip(shell),
        component(IconButton, { icon: 'settings', label: t('home.settings'), outlined: true, class: 'open-settings', onClick: () => { shell.go('settings'); } }),
        // Part 36a: the account (its settings section) once accounts are on.
        when(
          () => shell.account.status().kind === 'off',
          () => component(IconButton, { icon: 'user', label: t('home.laterName', { name: t('home.account') }), outlined: true, class: 'account-later', 'aria-disabled': 'true' }),
          () => component(IconButton, { icon: 'user', label: t('home.account'), outlined: true, class: 'open-account', onClick: () => { openAccountSettings(shell); } }),
        ),
      ];
    },
  });
}

/** H4: where the writer was in the open book, and when they last wrote in it (nothing before its chapters are read). */
export function whereText(shell: Shell, bookId: string): string {
  const { t, workspace, locale } = shell;
  const chapters = workspace.listing()?.chapters ?? [];
  const index = chapters.findIndex((entry) => entry.id === workspace.chapterId());
  const entry = chapters[index];
  const where = entry === undefined ? '' : t('home.chapter', { number: index + 1, title: shownTitle(shell, entry) });
  const edited = workspace.writing(bookId).edited;
  if (edited === null) return where;
  const when = editedWhen(edited, shell.now(), shell.zone);
  const time = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(when.at);
  const said =
    when.kind === 'today' ? t('home.today', { time }) : when.kind === 'yesterday' ? t('home.yesterday', { time }) : t('home.onDay', { date: new Intl.DateTimeFormat(locale, { dateStyle: 'long' }).format(when.at) });
  return where === '' ? said : t('home.edited', { where, when: said });
}

/** H4: the book's words toward its goal, when it has one and its words are known (the library's ProgressBar). */
function Progress(shell: Shell, bookId: () => string): Child {
  const { t, workspace } = shell;
  const writing = (): { readonly words: number; readonly goal: number } | null => {
    const { words, bookGoal } = workspace.writing(bookId());
    return words === null || bookGoal === null ? null : { words, goal: bookGoal };
  };
  return whenSome(writing, (now) =>
    component(ProgressBar, {
      class: 'lib-hero__progress',
      get value() {
        return Math.min(now().words, now().goal);
      },
      get max() {
        return now().goal;
      },
      get label() {
        return t('home.words', { count: now().words });
      },
      get valueText() {
        return t('home.goal', { goal: now().goal });
      },
      ariaLabel: t('home.progress'),
      get ariaValueText() {
        return t('home.progressText', { words: now().words, goal: now().goal });
      },
    }),
  );
}

/**
 * H4: the banner: the open book's cover, title, place and progress, and its three actions; made once, following the open
 * book as it changes, so the focus on its actions stays.
 */
function Banner(shell: Shell): Child {
  const { t, workspace, ui } = shell;
  return whenSome(workspace.book, (book) =>
    el(
      'section',
      { class: 'lib-hero', 'aria-labelledby': 'home-title' },
      BannerCover(shell, book),
      el(
        'div',
        { class: 'lib-hero__body' },
        el('p', { class: 'c-eyebrow' }, t('home.eyebrow')),
        el('h1', { id: 'home-title', class: 'lib-hero__title', tabIndex: -1 }, () => shownBook(shell, book())),
        el('p', { class: 'lib-hero__meta' }, () => whereText(shell, book().id)),
        Progress(shell, () => book().id),
        el(
          'div',
          { class: 'lib-hero__actions' },
          component(Button, { variant: 'primary', size: 'lg', iconStart: 'play', class: 'continue-writing', onClick: () => { continueWriting(shell); }, children: t('home.continue') }),
          ExportMenu(shell, { class: 'home-export', variant: 'secondary', size: 'lg', label: t('home.export') }),
          component(Button, { size: 'lg', iconStart: 'edit', class: 'book-details', 'aria-haspopup': 'dialog', onClick: () => ui.setDialog({ kind: 'book' }), children: t('home.details') }),
        ),
      ),
    ),
  );
}

/**
 * H5, H7: a book on the shelf, kept by its place (the list is read again after every save, so the card, and the focus on
 * it, stays); the whole card opens it (the library's BookCard, a button here: the address stays the app's).
 */
function ShelfBook(shell: Shell, book: () => BookSummary): Child {
  const { workspace, backups } = shell;
  const cover = coverOf(shell, book);
  return component(BookCard, {
    class: 'shelf-book',
    get title() {
      return shownBook(shell, book());
    },
    get words() {
      return workspace.writing(book().id).words ?? undefined;
    },
    get note() {
      return ageText(shell, backups.age(book().id));
    },
    get cover() {
      return { 'data-cover': String(cover.index), bg: cover.bg, ink: cover.ink, src: cover.src() ?? undefined, overlayTitle: true };
    },
    get disabled() {
      return !workspace.editable();
    },
    onClick: () => {
      openBook(shell, book().id);
    },
  });
}

/** H5, H6: My books, in the order chosen, and the New book tile at the end (the library's BookRow). */
function Shelf(shell: Shell): Child {
  const { t, workspace, locale } = shell;
  const sort = (): ShelfSort => workspace.settings().shelfSort;
  const books = createMemo(() => shelfOrder(workspace.books().map((book) => ({ id: book.id, title: shownBook(shell, book), book })), workspace.writing, sort(), locale).map((each) => each.book));
  const words: Readonly<Record<ShelfSort, string>> = { edited: t('home.sortEdited'), title: t('home.sortTitle'), words: t('home.sortWords') };
  return component(BookRow, {
    class: 'home-shelf',
    id: 'home-books',
    tabIndex: -1,
    title: t('home.myBooks'),
    get action() {
      return el(
        'label',
        { class: 'lib-sort' },
        t('home.sort'),
        component(Select, {
          size: 'sm',
          options: SHELF_SORTS.map((each) => ({ value: each, label: words[each] })),
          get value() {
            return sort();
          },
          onChange: (picked: string) => {
            const chosen = SHELF_SORTS.find((each) => each === picked);
            if (chosen !== undefined) void workspace.updateSettings({ shelfSort: chosen });
          },
        }),
      );
    },
    get children() {
      return [
        indexed(books, (book) => ShelfBook(shell, book)),
        component(NewBookTile, {
          class: 'new-book-tile',
          label: t('home.newTile'),
          hint: t('home.newTileHint'),
          disabled: () => !workspace.writable(),
          onClick: () => {
            createBook(shell);
          },
        }),
      ];
    },
  });
}

/**
 * The home page (H1–H9): its header, and the banner, the shelf and Shared with me (part 38c) as its main content; at
 * `#/shared` the same page, Shared with me current. A guest who never started a trial sees only Shared with me
 * (decision 75).
 */
export function Home(shell: Shell, statuses: () => Child): Child {
  const guest = (): boolean => onlyShared(shell);
  const current = (): 'home' | 'shared' => (shell.ui.route() === 'shared' ? 'shared' : 'home');
  return el(
    'div',
    { class: 'home' },
    el('a', { class: 'skip', href: () => (guest() ? '#home-shared' : '#home-books') }, shell.t('home.skip')),
    Header(shell, current),
    statuses(),
    el('main', { class: 'home-main' }, when(() => !guest(), () => [Banner(shell), Shelf(shell)]), SharedSection(shell)),
  );
}
